/**
 * The HTTP client.
 *
 * One place knows the base path, the token, the refresh dance and the error
 * envelope. A screen never touches fetch, never reads a status code, and
 * never branches on a Persian message: it branches on `error.code`.
 */

export const API_BASE = '/api/v1'

/**
 * Which app is asking. The member site and the operator console are served
 * from one host, and a cookie is scoped by host and path, never by port or by
 * app. Each app names itself on every call and the API keeps one refresh
 * cookie per app, so signing in to the console leaves the member tab alone.
 */
export type ClientApp = 'member' | 'admin'
const CLIENT_APP_HEADER = 'x-client-app'
let clientApp: ClientApp = 'member'

/** Called once, before the first request, by the app's entry. */
export function setClientApp(app: ClientApp): void {
  clientApp = app
  siblings()
}

export interface ApiErrorPayload {
  code: string
  message: string
  /** Rendered beside the input it names, never in a banner over the form. */
  fields?: Record<string, string>
  /** retryAfterSeconds, lockedUntil, requiredStatus, missingPermission, caseNo. */
  meta?: Record<string, unknown>
  traceId: string
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly fields?: Record<string, string>
  readonly traceId: string
  readonly meta?: Record<string, unknown>

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message)
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code
    this.fields = payload.fields
    this.traceId = payload.traceId
    this.meta = payload.meta
  }

  is(code: string): boolean {
    return this.code === code
  }

  /** Seconds to wait before retrying, when the server said so. */
  get retryAfterSeconds(): number | null {
    const v = this.meta?.retryAfterSeconds
    return typeof v === 'number' ? v : null
  }
}

const NETWORK_ERROR: ApiErrorPayload = {
  code: 'NETWORK',
  message: 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.',
  traceId: '-',
}

type TokenListener = (token: string | null) => void

class TokenStore {
  private access: string | null = null
  private listeners = new Set<TokenListener>()

  get(): string | null {
    return this.access
  }

  set(token: string | null) {
    this.access = token
    for (const l of this.listeners) l(token)
  }

  subscribe(l: TokenListener): () => void {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }
}

export const tokens = new TokenStore()

/**
 * A signed in member whose session could not be renewed. Said once per lapse,
 * however many queries were in flight when it happened, and never for a
 * visitor who was not signed in to begin with.
 */
type ExpiryListener = () => void
const expiryListeners = new Set<ExpiryListener>()
let expiryAnnounced = false
tokens.subscribe((token) => {
  if (token) expiryAnnounced = false
})

function announceExpired() {
  if (expiryAnnounced) return
  expiryAnnounced = true
  for (const l of expiryListeners) l()
}

export function onSessionExpired(listener: ExpiryListener): () => void {
  expiryListeners.add(listener)
  return () => expiryListeners.delete(listener)
}

/**
 * How a renewal ended. Only a refusal ends the session. A renewal that never
 * reached the server, or reached it while it was failing, says nothing about
 * the refresh cookie, which is still there for the next attempt.
 */
type RefreshOutcome = 'ok' | 'refused' | 'unavailable'

let refreshInFlight: Promise<RefreshOutcome> | null = null

/**
 * The tabs of one app share one refresh cookie, and the server takes a
 * rotated token presented twice as stolen: it revokes the family and signs
 * the account out everywhere. So tabs renew one at a time, under a lock the
 * browser holds across them, and the tab that renewed hands its access token
 * to the others. Every renewal also ends the session the previous token
 * belonged to, so a tab that kept its own token would lose it to the next
 * sibling that renewed, and the two would take turns renewing on every call.
 */
type SiblingNotice = { kind: 'renewing' } | { kind: 'renewed'; token: string | null }

/**
 * The browser offers the lock only on https and on localhost. Without it, a
 * tab that heard a sibling start a renewal waits up to this long for the
 * answer instead of racing it. That narrows the race to two tabs starting in
 * the same instant; only the lock closes it.
 */
const SIBLING_WAIT_MS = 10_000

let siblingChannel: BroadcastChannel | null = null
let siblingChannelApp: ClientApp | null = null
let siblingRenewingUntil = 0
const siblingWaiters = new Set<() => void>()

/**
 * The channel this app's tabs talk on. Opened as the module loads, and again
 * when the app names itself, so a tab hears a sibling's renewal before it has
 * sent anything of its own.
 */
function siblings(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (siblingChannel && siblingChannelApp === clientApp) return siblingChannel
  siblingChannel?.close()
  siblingChannel = new BroadcastChannel(`metal-session-${clientApp}`)
  siblingChannelApp = clientApp
  siblingChannel.onmessage = (event: MessageEvent<SiblingNotice>) => heard(event.data)
  return siblingChannel
}
siblings()

function heard(notice: SiblingNotice) {
  if (notice.kind === 'renewing') {
    siblingRenewingUntil = Date.now() + SIBLING_WAIT_MS
    return
  }
  siblingRenewingUntil = 0
  // Taken by a tab that is signed in, or that is renewing right now: either
  // way it rides the same cookie. A tab that was signed out stays signed out
  // until it asks.
  const current = tokens.get()
  if (notice.token && notice.token !== current && (current !== null || refreshInFlight)) {
    tokens.set(notice.token)
  }
  for (const wake of siblingWaiters) wake()
}

/** Resolves once no sibling is known to be renewing. */
function siblingSettled(): Promise<void> {
  const wait = siblingRenewingUntil - Date.now()
  if (wait <= 0) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer)
      siblingWaiters.delete(done)
      resolve()
    }
    const timer = window.setTimeout(done, wait)
    siblingWaiters.add(done)
  })
}

/**
 * One renewal at a time across the app's tabs. The lock can reach the next
 * tab in line before the last tab's token does, so that tab first waits out
 * a renewal it heard start. Should the token still not come, it renews once
 * more on the cookie the last tab left behind, which is fresh and safe to
 * present.
 */
async function oneAtATime(task: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
  const run = async () => {
    await siblingSettled()
    return task()
  }
  const locks = (navigator as { locks?: LockManager }).locks
  return locks ? locks.request(`metal-refresh-${clientApp}`, run) : run()
}

/** A token newer than the one a refused request carried. */
function renewedSince(sent: string | null): boolean {
  const current = tokens.get()
  return current !== null && current !== sent
}

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers
 * share one request, otherwise a page with six queries fires six refreshes
 * and five of them race to invalidate the winner.
 *
 * `sent` is the token the refused request carried. When the token has moved
 * on since, this tab or a sibling has already renewed, and the request only
 * needs sending again.
 */
async function refresh(sent: string | null): Promise<RefreshOutcome> {
  if (renewedSince(sent)) return 'ok'
  if (!refreshInFlight) {
    // Asked again under the lock: a sibling may have renewed while this tab
    // waited for it.
    const task = async (): Promise<RefreshOutcome> => (renewedSince(sent) ? 'ok' : renew())
    refreshInFlight = oneAtATime(task).finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/** The refresh route itself, and word of it to the siblings. */
async function renew(): Promise<RefreshOutcome> {
  const channel = siblings()
  channel?.postMessage({ kind: 'renewing' } satisfies SiblingNotice)

  let outcome: RefreshOutcome
  let token: string | null = null
  try {
    const res = await fetch(`${API_BASE}/auth/session/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', [CLIENT_APP_HEADER]: clientApp },
    })
    if (res.ok) {
      token = ((await res.json()) as { accessToken: string }).accessToken
      outcome = 'ok'
    } else {
      // The route refuses with 401 or 403. Anything else is the server, or
      // the way to it, failing.
      outcome = res.status === 401 || res.status === 403 ? 'refused' : 'unavailable'
    }
  } catch {
    outcome = 'unavailable'
  }

  if (outcome === 'ok') tokens.set(token)
  if (outcome === 'refused') tokens.set(null)
  channel?.postMessage({ kind: 'renewed', token } satisfies SiblingNotice)
  return outcome
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Skip the automatic refresh and retry. Used by the auth routes themselves. */
  raw?: boolean
  /**
   * Send no token and never renew. For a public read a timer makes on its
   * own: it must not keep a session alive that nobody is using, or a
   * console left open never reaches its idle limit.
   */
  anonymous?: boolean
  headers?: Record<string, string>
}

/**
 * One fetch with the token, renewed once on a 401. Every route through this
 * file ends here, so a JSON read, an upload and a file download all survive
 * an access token that lapsed while the page was open.
 */
async function authorizedFetch(
  url: string,
  init: RequestInit,
  raw = false,
  anonymous = false,
): Promise<Response> {
  const send = async (token: string | null): Promise<Response> => {
    try {
      return await fetch(url, {
        ...init,
        credentials: 'include',
        headers: {
          [CLIENT_APP_HEADER]: clientApp,
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...(init.headers as Record<string, string> | undefined),
        },
      })
    } catch {
      throw new ApiError(0, NETWORK_ERROR)
    }
  }

  if (anonymous) return send(null)

  const sent = tokens.get()
  const res = await send(sent)
  if (res.status !== 401 || raw) return res

  const outcome = await refresh(sent)
  // Nobody answered for the cookie, so the session stands. The request fails
  // the way any dropped connection does, and its query retries, instead of
  // signing somebody out over a bad line.
  if (outcome === 'unavailable') throw new ApiError(0, NETWORK_ERROR)
  if (outcome === 'refused') {
    if (sent !== null) announceExpired()
    return res
  }
  return send(tokens.get())
}

/** A refusal read off a response that did not succeed. */
async function refusal(res: Response, fallback: string): Promise<ApiError> {
  const text = await res.text().catch(() => '')
  let envelope: ApiErrorPayload | null = null
  try {
    envelope = text ? (JSON.parse(text) as ApiErrorPayload) : null
  } catch {
    envelope = null
  }
  return new ApiError(
    res.status,
    envelope?.code ? envelope : { code: 'INTERNAL', message: fallback, traceId: '-' },
  )
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, raw, anonymous, headers = {} } = options
  const url = new URL(`${API_BASE}${path}`, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
    }
  }

  const res = await authorizedFetch(
    url.toString(),
    {
      method,
      signal,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
    raw,
    anonymous,
  )

  if (res.status === 204) return undefined as T
  if (!res.ok) throw await refusal(res, 'خطایی رخ داد. دوباره تلاش کنید.')

  const text = await res.text()
  return (text ? JSON.parse(text) : null) as T
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  del: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/**
 * Upload one or more files as multipart. The JSON client cannot carry bytes.
 *
 * `fieldName` is the name the receiving route's interceptor listens on. Every
 * upload route in the API listens on `file`, so that is the default; a route
 * that ever listens on something else says so at the call site. Sending a
 * part under a name the interceptor does not expect is rejected by multer
 * before the handler runs, which reads as an unexplained failure rather than
 * as a refusal, so the default has to be the one the routes actually use.
 */
export async function upload<T>(
  path: string,
  files: File[],
  fields: Record<string, string> = {},
  fieldName = 'file',
): Promise<T> {
  const form = new FormData()
  for (const f of files) form.append(fieldName, f)
  for (const [k, v] of Object.entries(fields)) form.append(k, v)

  const res = await authorizedFetch(`${API_BASE}${path}`, { method: 'POST', body: form })
  if (!res.ok) throw await refusal(res, 'بارگذاری فایل انجام نشد. دوباره تلاش کنید.')
  const text = await res.text()
  return (text ? JSON.parse(text) : null) as T
}

/**
 * A protected file, as bytes. A plain link carries no token and the API
 * refuses it, so a screen that shows a document fetches it here and opens the
 * blob instead.
 */
export async function download(path: string): Promise<Blob> {
  const res = await authorizedFetch(`${API_BASE}${path}`, { method: 'GET' })
  if (!res.ok) throw await refusal(res, 'دریافت فایل انجام نشد. دوباره تلاش کنید.')
  return res.blob()
}

/**
 * Open a protected file, a KYC scan, a brand or offer attachment, in a new tab.
 *
 * A plain link carries no token and the API refuses it, so the bytes come
 * through `download()` and the tab shows the blob. The tab itself is opened
 * first, before anything is awaited, because a browser lets a page open one
 * only while the click that asked for it is fresh: Safari refuses once
 * anything has been awaited, and the others after a few seconds, which a
 * large scan on a slow line outlasts. It is opened blank and without `noopener`, which would
 * make `window.open` return null whether or not the tab appeared, and is cut
 * loose from this page by hand instead.
 *
 * Call it straight from the click handler. Resolves false when the browser
 * blocked the tab; on a failed download it closes the tab and throws.
 */
export async function openProtectedFile(path: string): Promise<boolean> {
  const tab = window.open('', '_blank')
  if (!tab) return false
  tab.opener = null
  let blob: Blob
  try {
    blob = await download(path)
  } catch (e) {
    tab.close()
    throw e
  }
  const url = URL.createObjectURL(blob)
  if (tab.closed) {
    URL.revokeObjectURL(url)
    return true
  }
  tab.location.href = url
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return true
}

/**
 * The collection envelope. The server reports the total so a footer can say
 * «۷ از ۶۰ آگهی» without counting rows it cannot see. `page` is 1-based.
 */
export interface Paged<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  pageCount: number
  facets?: Record<string, unknown>
}
